// Dutch and English from day one (apps/mobile/README.md). Strings live in lib/locales, never in
// components; the locale is a tiny external store persisted in localStorage. Dutch is bundled (it is
// the default and every locale's fallback); the others load on demand, and main.tsx waits for the
// stored one before the first render, so a screen never shows a key or a flash of the wrong language.
import { useCallback, useMemo, useSyncExternalStore } from "react";
import { nl } from "./locales/nl";

export const LOCALES = ["nl", "en", "de", "fr", "it", "es", "pt"] as const;
export type Locale = (typeof LOCALES)[number];

/** Native display name per locale, for a language picker (About.tsx). */
export const LOCALE_NAMES: Record<Locale, string> = {
  nl: "Nederlands",
  en: "English",
  de: "Deutsch",
  fr: "Français",
  it: "Italiano",
  es: "Español",
  pt: "Português",
};

/** The flag shown for each locale (the domain's lexicon/countries.ts, keyed by
 * ISO country -- a language isn't a country, so this is a UI convenience,
 * not a claim). English maps to GB rather than US, matching the app's own
 * "en" wording (British, not American, spelling throughout). */
export const LOCALE_FLAG: Record<Locale, string> = {
  nl: "NL",
  en: "GB",
  de: "DE",
  fr: "FR",
  it: "IT",
  es: "ES",
  pt: "PT",
};

const STORAGE_KEY = "proef.locale";

export type MessageKey = keyof typeof nl;
/** A non-Dutch dictionary: any key it lacks reads Dutch (the Academy's copy exists in nl and en only). */
export type Messages = Readonly<Partial<Record<MessageKey, string>>>;

/** One literal import per locale, so each dictionary is its own chunk. */
async function importLocale(locale: Exclude<Locale, "nl">): Promise<Messages> {
  switch (locale) {
    case "en":
      return (await import("./locales/en")).en;
    case "de":
      return (await import("./locales/de")).de;
    case "fr":
      return (await import("./locales/fr")).fr;
    case "it":
      return (await import("./locales/it")).it;
    case "es":
      return (await import("./locales/es")).es;
    // "pt", the one locale left.
    default:
      return (await import("./locales/pt")).pt;
  }
}
const loaded: Partial<Record<Locale, Messages>> = { nl };
const loading = new Map<Locale, Promise<void>>();

export function isLocaleLoaded(locale: Locale): boolean {
  return loaded[locale] !== undefined;
}

/** Fetches a locale's dictionary once; a failed fetch is forgotten so a later call can retry. */
export function loadLocale(locale: Locale): Promise<void> {
  if (locale === "nl" || loaded[locale] !== undefined) return Promise.resolve();
  let pending = loading.get(locale);
  if (pending === undefined) {
    pending = (async () => {
      try {
        loaded[locale] = await importLocale(locale);
      } catch (error) {
        loading.delete(locale);
        throw error;
      }
    })();
    loading.set(locale, pending);
  }
  return pending;
}

/**
 * Casts a dynamically-built key (e.g. `` `note.quality.${quality}` ``) to
 * MessageKey, isolated to this one function so every call site that builds
 * a key from a known enum value (always a real dictionary entry, added
 * alongside that enum -- see the dictionary's own "note.", "karaat." and
 * "blind." key groups) doesn't repeat the same oxlint suppression.
 */
export function mk(key: string): MessageKey {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return key as MessageKey;
}

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

function readStored(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored !== null && isLocale(stored)) return stored;
  } catch {
    // Storage can be blocked (private mode); default below.
  }
  if (typeof navigator === "undefined") return "nl";
  const lang = navigator.language.toLowerCase().slice(0, 2);
  return isLocale(lang) ? lang : "nl";
}

let current: Locale = typeof window === "undefined" ? "nl" : readStored();
const listeners = new Set<() => void>();

let requested: Locale = current;

function apply(locale: Locale): void {
  current = locale;
  document.documentElement.lang = locale;
  listeners.forEach((l) => l());
}

/** Switches language once its dictionary is there; a later choice wins over one still loading. */
export function setLocale(locale: Locale): void {
  requested = locale;
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Not persisting is fine.
  }
  if (isLocaleLoaded(locale)) {
    apply(locale);
    return;
  }
  void (async () => {
    try {
      await loadLocale(locale);
    } catch {
      // Offline with the chunk not cached yet: the current language stays until the next choice.
      return;
    }
    if (requested === locale) apply(locale);
  })();
}

export function getLocale(): Locale {
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Translate `key`, substituting `{name}` placeholders from `vars`. */
export function translate(
  locale: Locale,
  key: MessageKey,
  vars?: Record<string, string | number>,
): string {
  // A key the locale lacks, or a locale not loaded yet, reads Dutch: never a bare key.
  const table: Messages = loaded[locale] ?? nl;
  let text: string = table[key] ?? nl[key];
  if (vars !== undefined) {
    for (const [name, value] of Object.entries(vars))
      text = text.replaceAll(`{${name}}`, String(value));
  }
  return text;
}

export function useT(): {
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
  locale: Locale;
} {
  // useSyncExternalStore's getServerSnapshot needs to agree with getLocale's
  // own Locale return type; TS widens the bare literal here without help.
  // oxlint-disable-next-line typescript/no-unnecessary-type-assertion
  const locale = useSyncExternalStore(subscribe, getLocale, () => "nl" as Locale);
  // Stable across renders as long as `locale` doesn't change: every caller
  // that lists `t` in a useEffect/useCallback dependency array (there are
  // many) would otherwise see a new function identity on every render and
  // re-run forever, since a render this hook is called from is often itself
  // the result of that same effect's own state update.
  const t = useCallback(
    (key: MessageKey, vars?: Record<string, string | number>) => translate(locale, key, vars),
    [locale],
  );
  return useMemo(() => ({ t, locale }), [t, locale]);
}
